import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { TimeoutError as RxTimeoutError, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 250;

/**
 * Retries idempotent (`GET`) requests once on transient failure. Mutating
 * verbs are never retried — replaying a `POST`/`PUT`/`PATCH`/`DELETE` can
 * duplicate work the server has already begun.
 *
 * A "transient failure" is:
 *   - an RxJS `TimeoutError` (timeout window exceeded), or
 *   - an `HttpErrorResponse` with status 0 (network/CORS) or 5xx (server).
 *
 * 4xx errors are never retried — they reflect a client problem retrying
 * won't fix.
 */
export const httpRetryInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }
  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: error => (isRetriable(error) ? timer(RETRY_DELAY_MS) : throwError(() => error)),
    })
  );
};

function isRetriable(error: unknown): boolean {
  if (error instanceof RxTimeoutError) return true;
  if (error instanceof HttpErrorResponse) {
    return error.status === 0 || (error.status >= 500 && error.status < 600);
  }
  return false;
}
